import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  integrationGovernanceApi,
  type ContractValidationResult,
  type DependencyGraph,
  type IntegrationContract,
  type IntegrationGovernanceDashboard,
  type IntegrationPlatformHealth,
  type IntegrationPolicy,
  type RegisteredService,
  type VersionMatrixEntry,
} from '../../api/integration';

type Tab = 'overview' | 'services' | 'contracts' | 'dependencies' | 'health' | 'matrix' | 'policies';
type Theme = 'light' | 'dark';

function healthBadge(status: string, dark: boolean) {
  const ok = status === 'healthy';
  return ok
    ? dark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
    : dark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700';
}

function compatBadge(status: string, dark: boolean) {
  if (status === 'compatible') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'breaking') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-amber-400' : 'text-amber-600';
}

export function IntegrationGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<IntegrationGovernanceDashboard | null>(null);
  const [services, setServices] = useState<RegisteredService[]>([]);
  const [contracts, setContracts] = useState<IntegrationContract[]>([]);
  const [graph, setGraph] = useState<DependencyGraph | null>(null);
  const [health, setHealth] = useState<IntegrationPlatformHealth | null>(null);
  const [policies, setPolicies] = useState<IntegrationPolicy[]>([]);
  const [matrix, setMatrix] = useState<VersionMatrixEntry[]>([]);
  const [familyFilter, setFamilyFilter] = useState('');
  const [validateResult, setValidateResult] = useState<ContractValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, svc, ctr, dep, hlth, pol, ver] = await Promise.all([
        integrationGovernanceApi.dashboard(),
        integrationGovernanceApi.listServices(),
        integrationGovernanceApi.listContracts(),
        integrationGovernanceApi.dependencies(),
        integrationGovernanceApi.registryHealth(),
        integrationGovernanceApi.listPolicies(),
        integrationGovernanceApi.versionMatrix(),
      ]);
      setDashboard(dash);
      setServices(svc);
      setContracts(ctr);
      setGraph(dep);
      setHealth(hlth);
      setPolicies(pol);
      setMatrix(ver);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration governance');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validateContract = async (contractId: string) => {
    setBusy(true);
    setValidateResult(null);
    try {
      const result = await integrationGovernanceApi.validateContract({
        contract_id: contractId,
        consumer_service: 'demandplanning',
      });
      setValidateResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const families = [...new Set(services.map((s) => s.product_family).filter(Boolean))] as string[];
  const filteredServices = familyFilter
    ? services.filter((s) => s.product_family === familyFilter)
    : services;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Service Registry' },
    { id: 'contracts', label: 'Contract Explorer' },
    { id: 'dependencies', label: 'Dependency Explorer' },
    { id: 'health', label: 'Health Dashboard' },
    { id: 'matrix', label: 'Version Matrix' },
    { id: 'policies', label: 'Policy Manager' },
  ];

  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';
  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">DEV-000078</p>
          <h1 className="text-2xl font-semibold">Integration Governance</h1>
          <p className={`text-sm ${muted}`}>
            Service registry, contract validation, dependency graph & cross-service governance
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/enterprise-integration" className="text-indigo-500 hover:underline">
              Enterprise Integration Framework
            </Link>
            <span className={muted}>·</span>
            <Link to="/integrations" className="text-indigo-500 hover:underline">
              Connector Hub
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTheme(dark ? 'light' : 'dark')}
            className={`rounded-lg border px-3 py-2 text-sm ${card}`}
            aria-label="Toggle dark mode"
          >
            {dark ? 'Light' : 'Dark'}
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {error}
        </div>
      )}

      {validateResult && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${validateResult.valid ? (dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800') : (dark ? 'border-amber-900 bg-amber-950 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800')}`}>
          {validateResult.message}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-indigo-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Integration Health Score</p>
            <p className="mt-1 text-3xl font-semibold text-indigo-500">{health.integration_health_score.toFixed(1)}</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Registered Services</p>
            <p className="mt-1 text-3xl font-semibold">{health.registered_services}</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Active Contracts</p>
            <p className="mt-1 text-3xl font-semibold">{health.active_contracts}</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Contract Compliance</p>
            <p className="mt-1 text-3xl font-semibold">{health.contract_compliance.toFixed(1)}%</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>API Compatibility</p>
            <p className="mt-1 text-2xl font-semibold">{health.api_compatibility.toFixed(1)}%</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Dependency Stability</p>
            <p className="mt-1 text-2xl font-semibold">{health.dependency_stability.toFixed(1)}%</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Avg Latency</p>
            <p className="mt-1 text-2xl font-semibold">{health.avg_integration_latency_ms.toFixed(0)} ms</p>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Service Availability</p>
            <p className="mt-1 text-2xl font-semibold">{health.service_availability.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {tab === 'services' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="family-filter" className={`text-sm ${muted}`}>Product family</label>
            <select
              id="family-filter"
              value={familyFilter}
              onChange={(e) => setFamilyFilter(e.target.value)}
              className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}
            >
              <option value="">All families</option>
              {families.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-left text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr>
                  <th className="px-4 py-2 font-medium">Service</th>
                  <th className="px-4 py-2 font-medium">Version</th>
                  <th className="px-4 py-2 font-medium">Family</th>
                  <th className="px-4 py-2 font-medium">Health</th>
                  <th className="px-4 py-2 font-medium">Port</th>
                  <th className="px-4 py-2 font-medium">Dependencies</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((s) => (
                  <tr key={s.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="px-4 py-2 font-medium">{s.name}</td>
                    <td className="px-4 py-2">{s.version}</td>
                    <td className="px-4 py-2">{s.product_family ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${healthBadge(s.health_status, dark)}`}>
                        {s.health_status}
                      </span>
                    </td>
                    <td className="px-4 py-2">{s.port ?? '—'}</td>
                    <td className={`px-4 py-2 text-xs ${muted}`}>{s.dependencies?.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'contracts' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 font-medium">Contract</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Producer</th>
                <th className="px-4 py-2 font-medium">Consumers</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.name}</div>
                    <div className={`text-xs ${muted}`}>v{c.version}</div>
                  </td>
                  <td className="px-4 py-2">{c.type}</td>
                  <td className="px-4 py-2">{c.producer_service}</td>
                  <td className={`px-4 py-2 text-xs ${muted}`}>{c.consumer_services?.join(', ')}</td>
                  <td className={`px-4 py-2 font-medium ${compatBadge(c.compatibility_status, dark)}`}>
                    {c.compatibility_status}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => validateContract(c.id)}
                      className="text-xs text-indigo-500 hover:underline disabled:opacity-50"
                    >
                      Validate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dependencies' && graph && (
        <div className="space-y-4">
          {graph.circular_dependencies?.length > 0 && (
            <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-amber-900 bg-amber-950 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              Circular dependencies detected: {graph.circular_dependencies.join('; ')}
            </div>
          )}
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-sm ${muted}`}>{graph.edges.length} dependency edges across {graph.services.length} services</p>
            <ul className="mt-3 max-h-96 space-y-1 overflow-y-auto text-sm">
              {graph.edges.map((e, i) => (
                <li key={`${e.from}-${e.to}-${i}`} className="font-mono text-xs">
                  <span className="text-indigo-500">{e.from}</span>
                  <span className={muted}> → </span>
                  <span>{e.to}</span>
                  {e.critical && <span className={`ml-2 ${dark ? 'text-red-400' : 'text-red-600'}`}>critical</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'health' && health && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold text-indigo-500">{health.integration_health_score.toFixed(1)}</div>
            <div>
              <p className="font-semibold">Integration Health Score</p>
              <p className={`text-sm ${muted}`}>Validated {new Date(health.validated_at).toLocaleString()}</p>
            </div>
          </div>
          <dl className="mt-6 grid gap-3 sm:grid-cols-2">
            <div><dt className={`text-xs uppercase ${muted}`}>Breaking Changes</dt><dd className="font-medium">{health.breaking_changes}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Deprecated Services</dt><dd className="font-medium">{health.deprecated_services}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Contract Compliance</dt><dd className="font-medium">{health.contract_compliance.toFixed(1)}%</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Dependency Stability</dt><dd className="font-medium">{health.dependency_stability.toFixed(1)}%</dd></div>
          </dl>
        </div>
      )}

      {tab === 'matrix' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 font-medium">Service</th>
                <th className="px-4 py-2 font-medium">Contract</th>
                <th className="px-4 py-2 font-medium">Current</th>
                <th className="px-4 py-2 font-medium">Min Consumer</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((m, i) => (
                <tr key={`${m.service}-${m.contract_name}-${i}`} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">{m.service}</td>
                  <td className="px-4 py-2">{m.contract_name}</td>
                  <td className="px-4 py-2">{m.current_version}</td>
                  <td className="px-4 py-2">{m.min_consumer_version}</td>
                  <td className={`px-4 py-2 font-medium ${compatBadge(m.status, dark)}`}>{m.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'policies' && (
        <div className="grid gap-4 md:grid-cols-2">
          {policies.map((p) => (
            <div key={p.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{p.name}</p>
                  <p className={`text-xs uppercase ${muted}`}>{p.policy_type}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs ${p.enabled ? (dark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : (dark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500')}`}>
                  {p.enabled ? 'enabled' : 'disabled'}
                </span>
              </div>
              <p className={`mt-2 text-xs ${muted}`}>Applies to: {p.applies_to?.join(', ')}</p>
              {p.retry_max > 0 && <p className={`mt-1 text-xs ${muted}`}>Retry max: {p.retry_max}</p>}
              {p.timeout_ms > 0 && <p className={`mt-1 text-xs ${muted}`}>Timeout: {p.timeout_ms} ms</p>}
              {p.rate_limit_rps > 0 && <p className={`mt-1 text-xs ${muted}`}>Rate limit: {p.rate_limit_rps} rps</p>}
            </div>
          ))}
        </div>
      )}

      {dashboard && tab === 'overview' && (
        <p className={`text-xs ${muted}`}>Tenant {dashboard.tenant_id} · {services.length} services · {contracts.length} contracts</p>
      )}
    </div>
  );
}
